import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { DetailList, ListRow, SectionHeader } from '@/components/banking';
import { resolveContext } from '@/state/session';
import { usePrefersReducedMotion } from '@/hooks/useAsync';

/**
 * Profile.
 *
 * Deliberately separate from Security: security mechanisms are consolidated on
 * the Security screen (TT-R143-02), so this page carries identity, contact and
 * presentation preferences only and links across rather than duplicating.
 */
export function ProfilePage() {
  const resolved = resolveContext();
  const reducedMotion = usePrefersReducedMotion();

  if (!resolved) return null;

  const roleLabel: Record<string, string> = {
    role_owner: 'Business owner',
    role_finance: 'Finance administrator',
    role_approver: 'Payment approver',
    role_employee: 'Employee / cardholder',
    role_readonly: 'Read-only finance user',
  };

  return (
    <>
      <MobileHeader title="Profile" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <section aria-labelledby="profile-you" className="tm-card">
          <SectionHeader title="Your details" id="profile-you" />
          <DetailList
            items={[
              { label: 'Name', value: resolved.user.name },
              { label: 'Email', value: resolved.user.email },
              { label: 'Role', value: roleLabel[resolved.user.roleId] ?? resolved.user.roleId },
              { label: 'Company', value: resolved.company.tradingName },
            ]}
          />
        </section>

        <section aria-labelledby="profile-prefs">
          <SectionHeader title="Preferences" id="profile-prefs" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <ListRow
                  to="/mobile/settings/personalisation"
                  title="Home layout"
                  meta="Choose which widgets appear and in what order"
                />
              </li>
              <li>
                <ListRow
                  to="/mobile/notifications"
                  title="Notifications"
                  meta="Alerts about payments, cards and security"
                />
              </li>
              <li>
                <ListRow
                  title="Reduced motion"
                  meta={
                    reducedMotion
                      ? 'On — following your device setting'
                      : 'Off — following your device setting'
                  }
                />
              </li>
            </ul>
          </div>
          <p className="tm-hint" style={{ marginBlockStart: 8 }}>
            Motion follows your device accessibility setting. Change it in your system settings and
            this app follows immediately.
          </p>
        </section>

        <section aria-labelledby="profile-security">
          <SectionHeader title="Security" id="profile-security" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <ListRow
                  to="/mobile/security"
                  title="Passkeys, MFA and devices"
                  meta="All security settings in one place"
                />
              </li>
            </ul>
          </div>
        </section>

        <section aria-labelledby="profile-legal">
          <SectionHeader title="Legal and support" id="profile-legal" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <ListRow title="Terms of service" meta="The agreement covering your account" />
              </li>
              <li>
                <ListRow title="Privacy notice" meta="How your data is used" />
              </li>
              <li>
                <ListRow title="Contact support" meta="Weekdays 8am–8pm" />
              </li>
            </ul>
          </div>
          <p className="tm-hint" style={{ marginBlockStart: 8 }}>
            Tribe provides the payment technology behind this account. Your funds are held by the
            licensed institution named in your terms.
          </p>
        </section>
      </div>
    </>
  );
}
